import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import { Zap, Download, Clock, Edit3, Printer, Sparkles, Users, CheckCircle2, XCircle } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { DropZone } from '@/components/ui/DropZone';
import { usePayroll } from '@/context/AppContext';
import { useToast } from '@/hooks/useToast';
import { processAttendance, processSalary, buildPayroll, applyPayrollFilters } from '@/lib/payroll';

// Template XLSX generator — matches original payroll.html format
function makeTemplate(kind: 'attendance' | 'employees'): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  if (kind === 'attendance') {
    const data: any[][] = [
      ['شركتي — تقرير البصمة'],
      ['تاريخ التصدير: ' + new Date().toLocaleString('ar-EG', { numberingSystem: 'latn' })],
      [],
      [
        'ID',
        'Full Name',
        'Department',
        'Date',
        'Work Start Time',
        'Work End Time',
        'Clock-In Time',
        'Clock-Out Time',
        'Attendance Status',
        'Late Duration',
        'Early Leave Duration',
        'Overtime Duration',
      ],
      ['1', 'أحمد محمد', 'الإنتاج', '2026-01-01', '08:00', '16:00', '08:05', '16:30', 'Present', '00:05', '', '00:30'],
      ['1', 'أحمد محمد', 'الإنتاج', '2026-01-02', '08:00', '16:00', '08:00', '16:00', 'Present', '', '', ''],
      ['2', 'سارة علي', 'المبيعات', '2026-01-01', '09:00', '17:00', '09:35', '17:00', 'Present', '00:35', '', '01:00'],
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Attendance');
  } else {
    const data: any[][] = [
      ['ID', 'Name', 'Job', 'Salary / Day', 'Rate / Hour'],
      ['1', 'أحمد محمد', 'عامل إنتاج', 350, 43.75],
      ['2', 'سارة علي', 'موظفة مبيعات', 400, 50],
      ['3', 'محمد إبراهيم', 'محاسب', 500, 62.5],
    ];
    const ws = XLSX.utils.aoa_to_sheet(data);
    XLSX.utils.book_append_sheet(wb, ws, 'Employees');
  }
  return wb;
}

function downloadTemplate(kind: 'attendance' | 'employees') {
  const wb = makeTemplate(kind);
  XLSX.writeFile(wb, kind === 'attendance' ? 'template_attendance.xlsx' : 'template_employees.xlsx');
}

export function UploadPage() {
  const {
    attendanceFile,
    salaryFile,
    setAttendanceFile,
    setSalaryFile,
    setPayrollData,
    setPeriod,
    setRawById,
    setAdminAdj,
    skipRows,
    thresholds,
    earlyThresholds,
    overtimeMode,
    missDeductions,
    excludeLastDayMissPenalty,
    excludeEmployeesWithoutAttendance,
    excludeEmployeesWithZeroSalary,
  } = usePayroll();
  const [loading, setLoading] = useState(false);
  const [attCount, setAttCount] = useState<number | null>(null);
  const [salCount, setSalCount] = useState<number | null>(null);
  const { show } = useToast();
  const navigate = useNavigate();

  // ✅ عدّ الموظفين في كل ملف لما الملف أو skipRows يتغير
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!attendanceFile) {
        setAttCount(null);
        return;
      }
      try {
        const buf = await attendanceFile.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<any[]>(sheet, { header: 1, defval: '', raw: false });
        const dataRows = rows.slice(skipRows);
        if (dataRows.length === 0) {
          if (!cancelled) setAttCount(0);
          return;
        }
        const headerRow = dataRows[0] as any[];
        const idIdx = headerRow.findIndex((h: any) => String(h).trim() === 'ID');
        const ids = new Set<string>();
        for (let i = 1; i < dataRows.length; i++) {
          const row = dataRows[i];
          const id = idIdx >= 0 ? String(row[idIdx] || '').trim() : '';
          if (id) ids.add(id);
        }
        if (!cancelled) setAttCount(ids.size);
      } catch {
        if (!cancelled) setAttCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [attendanceFile, skipRows]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!salaryFile) {
        setSalCount(null);
        return;
      }
      try {
        const buf = await salaryFile.arrayBuffer();
        const wb = XLSX.read(buf, { type: 'array' });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<any>(sheet, { defval: '' });
        const ids = new Set<string>();
        rows.forEach((r: any) => {
          const id = String(r['ID'] ?? r['Id'] ?? r['id'] ?? '').trim();
          if (id) ids.add(id);
        });
        if (!cancelled) setSalCount(ids.size);
      } catch {
        if (!cancelled) setSalCount(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [salaryFile]);

  const countsMatch = attCount !== null && salCount !== null && attCount === salCount;

  const calculate = async () => {
    if (!attendanceFile || !salaryFile) {
      show('من فضلك ارفع الملفين الأول', 'error');
      return;
    }
    setLoading(true);
    try {
      const [attBuf, salBuf] = await Promise.all([
        attendanceFile.arrayBuffer(),
        salaryFile.arrayBuffer(),
      ]);
      const attWb = XLSX.read(attBuf, { type: 'array' });
      const salWb = XLSX.read(salBuf, { type: 'array' });
      const { records, period, rawById } = processAttendance(
        attWb,
        skipRows,
        thresholds,
        earlyThresholds,
        overtimeMode,
        missDeductions,
        excludeLastDayMissPenalty,
        excludeEmployeesWithoutAttendance,
      );
      const salaryRows = processSalary(salWb);
      const initialAdj: Record<string, number> = {};
      records.forEach((r) => (initialAdj[r.ID] = 0));
      const built = buildPayroll(records, salaryRows, initialAdj);
      // ✅ طبّق فلاتر عدم الحضور والمرتب = 0
      const data = applyPayrollFilters(built, {
        excludeEmployeesWithoutAttendance,
        excludeEmployeesWithZeroSalary,
      });
      // ✅ ثبّت admin adj على الـ IDs الموجودة بعد الفلتر
      const filteredIds = new Set(data.map((d: any) => d.ID));
      const finalAdj: Record<string, number> = {};
      Object.keys(initialAdj).forEach((id) => {
        if (filteredIds.has(id)) finalAdj[id] = initialAdj[id];
      });
      setPayrollData(data);
      setPeriod(period);
      setRawById(rawById);
      setAdminAdj(finalAdj);
      show(
        `✅ تم احتساب المرتبات لـ ${data.length} موظف — الفترة: ${period.start || '؟'} إلى ${period.end || '؟'}`,
      );
      navigate('/results');
    } catch (e: any) {
      show('خطأ: ' + e.message, 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-slide-up">
      {/* Hero */}
      <div className="text-center max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-gradient-to-l from-indigo-50 to-violet-50 dark:from-indigo-900/30 dark:to-violet-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-bold mb-4 border border-indigo-100 dark:border-indigo-800/40">
          <Sparkles size={12} />
          الإصدار 2.0 — واجهة ويب حديثة
        </div>
        <h2 className="text-3xl sm:text-4xl font-black gradient-text mb-3">ابدأ بحساب المرتبات</h2>
        <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base leading-relaxed">
          ارفع ملف البصمة وملف بيانات الموظفين واحصل على نتائج دقيقة خلال ثوان
        </p>
      </div>

      {/* Drop zones */}
      <div className="grid md:grid-cols-2 gap-5">
        <DropZone
          label="ملف البصمة (يومي / أسبوعي / شهري)"
          file={attendanceFile}
          onFile={setAttendanceFile}
          icon="clock"
        />
        <DropZone
          label="ملف بيانات الموظفين"
          file={salaryFile}
          onFile={setSalaryFile}
          icon="users"
        />
      </div>

      {/* Templates */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 -mt-2">
        <span className="text-xs text-slate-500 dark:text-slate-400">📥 حمّل نموذج ملف فارغ:</span>
        <Button variant="secondary" size="sm" onClick={() => downloadTemplate('attendance')}>
          <Download size={14} className="text-indigo-500" /> نموذج البصمة <span className="badge badge-indigo">xlsx</span>
        </Button>
        <Button variant="secondary" size="sm" onClick={() => downloadTemplate('employees')}>
          <Download size={14} className="text-emerald-500" /> نموذج الموظفين <span className="badge badge-emerald">xlsx</span>
        </Button>
      </div>

      {/* Employee count comparison */}
      <Card
        accent={countsMatch ? 'emerald' : 'rose'}
        className={countsMatch ? '!bg-emerald-50/40 dark:!bg-emerald-900/10' : '!bg-rose-50/40 dark:!bg-rose-900/10'}
      >
        <div className="flex items-center gap-3 flex-wrap">
          <div
            className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              countsMatch ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'
            }`}
          >
            {countsMatch ? <CheckCircle2 size={20} /> : <XCircle size={20} />}
          </div>
          <div className="flex-1 min-w-[200px]">
            <div
              className={`font-bold text-sm ${
                countsMatch ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'
              }`}
            >
              {countsMatch
                ? '✅ عدد الموظفين متطابق في الملفين'
                : '⚠️ عدد الموظفين مختلف بين الملفين'}
            </div>
            <div className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5">
              ملف البصمة يُحسب بناءً على عدد الـ IDs الفريدة (بعد تخطي {skipRows} صفوف)
            </div>
          </div>

          <div className="flex items-center gap-3">
            <CountChip
              label="البصمة"
              value={attCount}
              color={countsMatch ? 'emerald' : 'rose'}
            />
            <span
              className={`text-xl font-black ${
                countsMatch ? 'text-emerald-500' : 'text-rose-500'
              }`}
            >
              =
            </span>
            <CountChip
              label="المرتب"
              value={salCount}
              color={countsMatch ? 'emerald' : 'rose'}
            />
          </div>
        </div>
      </Card>

      {/* Feature pills */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { icon: Clock, label: 'تأخير وخروج مبكر', desc: 'لكل موظف', color: 'from-indigo-500 to-violet-500' },
          { icon: Zap, label: 'تقريب الإضافي', desc: '2/4/6/8 ساعة', color: 'from-amber-500 to-orange-500' },
          { icon: Edit3, label: 'تعديلات إدارية', desc: 'مرنة وسهلة', color: 'from-emerald-500 to-teal-500' },
          { icon: Printer, label: 'طباعة احترافية', desc: 'ظروف و A4', color: 'from-slate-700 to-slate-900' },
        ].map((f, i) => {
          const Icon = f.icon;
          return (
            <div key={i} className="glass rounded-xl p-3 flex items-start gap-3 hover:shadow-soft transition-all">
              <div
                className={`w-9 h-9 rounded-lg bg-gradient-to-br ${f.color} flex items-center justify-center text-white shrink-0`}
              >
                <Icon size={18} />
              </div>
              <div>
                <div className="font-bold text-sm text-slate-800 dark:text-slate-100">{f.label}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400">{f.desc}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <Button
        fullWidth
        size="lg"
        loading={loading}
        disabled={!attendanceFile || !salaryFile}
        onClick={calculate}
        className="text-base py-4 shadow-xl"
        leftIcon={<Zap size={20} />}
      >
        احسب المرتبات الآن
      </Button>
    </div>
  );
}

function CountChip({
  label,
  value,
  color,
}: {
  label: string;
  value: number | null;
  color: 'emerald' | 'rose';
}) {
  const styles =
    color === 'emerald'
      ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border-emerald-300 dark:border-emerald-700'
      : 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-rose-300 dark:border-rose-700';
  return (
    <div className={`px-3 py-1.5 rounded-xl border ${styles} text-center min-w-[90px]`}>
      <div className="text-[10px] font-bold opacity-80">{label}</div>
      <div className="text-xl font-black font-mono leading-tight">
        {value === null ? '—' : value.toLocaleString('en-US')}
      </div>
      <div className="text-[9px] opacity-70">موظف</div>
    </div>
  );
}
